// src/utils/FileManager.js — fully regenerated 2025-10-26 (FieldTicket: hostname subdomain)
import { baseUrl } from "../components/config";

export const API_BASE = `${baseUrl}/api/files.php`;
export const DEFAULT_TTL = "+5 minutes";
const FETCH_TIMEOUT_MS = 30000;

/*──────────────── helpers ───────────────────────────────────*/
function timeoutFetch(resource, options = {}, ms = FETCH_TIMEOUT_MS) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  return fetch(resource, { ...options, signal: ctrl.signal }).finally(() =>
    clearTimeout(id)
  );
}

function ensureTrailingSlash(s = "") {
  const t = String(s).replace(/^\/+|\/+$/g, "");
  return t ? `${t}/` : "";
}

/**
 * Resolve tenant **subdomain** (FieldTicket):
 * Priority:
 *  1) Hostname first label (e.g., stasney.ogfieldticket.ogpumper.net → "stasney")
 *  2) ?company= | ?sub= | ?tenant=
 *  3) /companyHome/:subdomain (fallback for dev tools)
 *  4) localStorage companySubdomain | companyName
 *  5) "default"
 */
export function companySlug() {
  if (typeof window !== "undefined") {
    try {
      const { hostname, search, pathname } = window.location;

      // 1) Host subdomain (works in production FieldTicket)
      const host = (hostname || "").replace(/^www\./, "");
      const first = host.split(".")[0];
      if (first && first !== "localhost") return first;

      // 2) Query hints
      const sp = new URLSearchParams(search);
      const qp = sp.get("company") || sp.get("sub") || sp.get("tenant");
      if (qp) return qp;

      // 3) Fallback path matcher for dev links like /companyHome/<sub>
      const m = pathname.match(/\/companyhome\/([^\/\?]+)/i);
      if (m && m[1]) return decodeURIComponent(m[1]);
    } catch {}
  }

  // 4) LocalStorage
  if (typeof localStorage !== "undefined") {
    const ls =
      localStorage.getItem("companySubdomain") ||
      localStorage.getItem("companyName");
    if (ls) return ls;
  }

  // 5) Default
  return "default";
}

/**
 * POST JSON to files.php with:
 * - auto-forwarded ?token=…
 * - always including company=<subdomain> unless caller overrides
 */
async function jsonPost(action, payload = {}, ms = FETCH_TIMEOUT_MS) {
  let merged = { action, ...payload };
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      const tkn = sp.get("token");
      if (tkn && !merged.token) merged.token = tkn;
      if (!merged.company) merged.company = companySlug();
    }
  } catch {}
  const res = await timeoutFetch(
    API_BASE,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(merged),
      credentials: "include",
    },
    ms
  );

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`API ${action} bad JSON: ${text?.slice(0, 300)}`);
  }

  if (!res.ok) {
    const msg = data?.error || `API ${action} failed: ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

/*──────────────── public API ────────────────────────────────*/

/**
 * Upload file to `<company>/Leases/<folder>/nice_name.ext`.
 * @param {File|Blob} file
 * @param {string}    folderPath  relative under "Leases/" (e.g., "Cook 111 #37A")
 * @param {string}    userId
 * @param {string}    company     optional; defaults to hostname subdomain
 * @returns {Promise<string>}     S3 key
 */
export async function uploadFile(
  file,
  folderPath = "",
  userId = "web",
  company
) {
  const rawName = file?.name || "file.bin";
  const dot = rawName.lastIndexOf(".");
  const baseRaw = dot === -1 ? rawName : rawName.slice(0, dot);
  const base =
    (baseRaw || "file")
      .replace(/[^\w\- ]+/g, "")
      .trim()
      .replace(/\s+/g, " ")
      .slice(0, 50) || "file";
  const ext = dot === -1 ? "" : rawName.slice(dot).toLowerCase();
  const stamp = new Date().toISOString().replace(/[:.]/g, "").slice(0, 15);
  const rand = Math.random().toString(36).slice(2, 8);
  const niceName = `${base}_${stamp}_${rand}${ext}`;
  const cleanFolder = String(folderPath || "").replace(/^\/+|\/+$/g, "");
  const contentType = file?.type || "application/octet-stream";

  // Prefix relative to company in the API
  const prefix = `Leases/${ensureTrailingSlash(cleanFolder)}`;

  const presign = await jsonPost("putSigned", {
    filename: niceName,
    prefix,
    company,
    userId,
    contentType,
  });

  const bytes =
    typeof file?.arrayBuffer === "function"
      ? new Uint8Array(await file.arrayBuffer())
      : new Uint8Array(0);

  await timeoutFetch(presign.request.url, {
    method: presign.request.method || "PUT",
    headers: presign.request.headers || {},
    body: bytes,
  });

  return presign.key; // "<company>/Leases/.../nice.ext"
}

/**
 * List items at `<company>/<prefix>` where prefix like "Leases/" or "Leases/Foo/".
 * @param {string} prefix
 * @param {string} company   optional; defaults to hostname subdomain
 */
export async function listFiles(prefix = "", company) {
  const norm = ensureTrailingSlash(prefix);
  const items = await jsonPost("list", { prefix: norm, company });
  // Folders first, then alpha
  items.sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return (a.Key || "").localeCompare(b.Key || "");
  });
  return items;
}

/**
 * Get a time-limited signed URL for an S3 key.
 * @param {string} key
 * @param {string} ttl
 */
export async function signedUrl(key, ttl = DEFAULT_TTL) {
  const { url } = await jsonPost("signedUrl", { key, ttl });
  return url;
}

/**
 * Fetch OCR text stored at `<company>/Leases/<relativeFolder>/payload.ocr.json`.
 * @param {string} relativeFolder - e.g., "Cook 111 #37A"
 * @param {string} company        - optional; defaults to hostname subdomain
 */
export async function folderOcrText(relativeFolder, company) {
  if (!relativeFolder) return "";
  const tenant = company || companySlug();
  const folder = ensureTrailingSlash(`Leases/${relativeFolder}`);
  const key = `${tenant}/${folder}payload.ocr.json`;
  try {
    const url = await signedUrl(key, DEFAULT_TTL);
    const res = await timeoutFetch(url);
    if (!res.ok) return "";
    const txt = await res.text();
    const parsed = JSON.parse(txt);
    return (parsed.text || "").toLowerCase();
  } catch {
    return "";
  }
}
